import React, { useEffect } from 'react';
import { X, ChevronLeft, ChevronRight, MapPin, Calendar, Users, Camera } from 'lucide-react';
import { Link } from 'react-router-dom';
import PhotoFrame from './PhotoFrame';
import PhotoComments from './PhotoComments';

// Shared full-screen lightbox for member photos (keyboard: Esc / arrows).
export default function PhotoLightbox({ items, index, onClose, onPrev, onNext, mediaUrl, comments = true, onCommentCount }) {
  useEffect(() => {
    if (index === null) return undefined;
    const k = (e) => { if (e.key === 'Escape') onClose(); if (e.key === 'ArrowLeft') onPrev(); if (e.key === 'ArrowRight') onNext(); };
    window.addEventListener('keydown', k); return () => window.removeEventListener('keydown', k);
  }, [index, onClose, onPrev, onNext]);
  if (index === null || !items[index]) return null;
  const p = items[index];
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.9)' }} onClick={onClose} data-testid="photo-lightbox">
      <button className="absolute top-5 right-5 text-white" onClick={onClose} data-testid="lightbox-close"><X size={30} /></button>
      <button className="absolute left-5 text-white" onClick={(e) => { e.stopPropagation(); onPrev(); }} data-testid="lightbox-prev"><ChevronLeft size={40} /></button>
      <div onClick={(e) => e.stopPropagation()} className="max-w-4xl w-full text-center max-h-[92vh] overflow-y-auto">
        <PhotoFrame src={mediaUrl(p.url)} display={p.display} alt={p.title} className={`w-full ${comments ? "h-[52vh]" : "h-[70vh]"} rounded shadow-2xl`} testId="lightbox-image" />
        <div className="text-left mt-4 mx-auto max-w-2xl text-cream" style={{ color: 'var(--cream)' }}>
          {p.title && <h3 className="font-title text-xl">{p.title}</h3>}
          {p.description && <p className="text-sm mt-1 opacity-90">{p.description}</p>}
          <div className="flex flex-wrap gap-4 text-xs mt-2 opacity-80">
            {p.member_name && <span>By {p.member_name}</span>}
            {p.date_taken && <span className="flex items-center gap-1"><Calendar size={12} /> {p.date_taken}</span>}
            {p.place && <span className="flex items-center gap-1"><MapPin size={12} /> {p.place}</span>}
            {p.camera && <span className="flex items-center gap-1"><Camera size={12} /> {p.camera}</span>}
            {p.people && (
              <span className="flex items-center gap-1 flex-wrap"><Users size={12} />
                {p.people.split(',').map((n) => n.trim()).filter(Boolean).map((n) => (
                  <Link key={n} to={`/members/photos?person=${encodeURIComponent(n)}`} onClick={onClose} className="underline decoration-dotted hover:opacity-80" title={`All photos featuring ${n}`} data-testid={`lightbox-person-${n}`}>{n}</Link>
                ))}
              </span>
            )}
          </div>
          {p.tags && p.tags.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-2">
              {p.tags.map((t, i) => <span key={i} className="text-xs px-2 py-0.5 rounded" style={{ background: 'rgba(255,255,255,0.15)' }}>#{t}</span>)}
            </div>
          )}
          {comments && <div className="mt-3 pt-3" style={{ borderTop: '1px solid rgba(255,255,255,0.15)' }}><PhotoComments photoId={p.id} onCountChange={onCommentCount} /></div>}
        </div>
      </div>
      <button className="absolute right-5 text-white" onClick={(e) => { e.stopPropagation(); onNext(); }} data-testid="lightbox-next"><ChevronRight size={40} /></button>
    </div>
  );
}
